// PWA install / service-worker integration.
//
// The browser fires `beforeinstallprompt` exactly once and only when its
// install heuristics are satisfied (valid manifest, registered SW, served
// over HTTPS or localhost, …). We capture it at module-load time so the
// event isn't lost before the UI is ready, then expose imperative helpers
// + a tiny pub/sub for views that want to react to availability changes.

let deferredPrompt = null;
const listeners = new Set();

function notify()
{
    listeners.forEach( cb =>
    {
        try { cb(); }
        catch ( err ) { console.error( 'pwa listener failed', err ); }
    });
}

// Run once, on first import — must register before window.onload so we don't
// miss the event.
( function captureInstallEvents()
{
    window.addEventListener( 'beforeinstallprompt', ( ev ) =>
    {
        // Stop the browser's mini-infobar; we surface our own button.
        ev.preventDefault();
        deferredPrompt = ev;
        notify();
    });

    window.addEventListener( 'appinstalled', () =>
    {
        deferredPrompt = null;
        notify();
    });
})();


/**
 * True when the page is being rendered as an installed PWA (Android/Chrome
 * "standalone" display-mode, iOS Safari `navigator.standalone`).
 */
export function isInstalled()
{
    if ( typeof window === 'undefined' ) return false;

    if ( window.matchMedia )
    {
        if ( window.matchMedia( '(display-mode: standalone)' ).matches )    return true;
        if ( window.matchMedia( '(display-mode: fullscreen)' ).matches )    return true;
        if ( window.matchMedia( '(display-mode: window-controls-overlay)' ).matches ) return true;
    }
    if ( window.navigator && window.navigator.standalone === true ) return true;

    return false;
}

/**
 * True when the browser has fired beforeinstallprompt and the app isn't
 * already installed — i.e., we have a prompt we can trigger.
 */
export function canInstall()
{
    return !!deferredPrompt && !isInstalled();
}

/**
 * Trigger the deferred install prompt. Resolves with the user's outcome
 * ('accepted' | 'dismissed' | 'unavailable'). The prompt can only be used
 * once; whichever way the user goes, we clear it.
 */
export async function triggerInstall()
{
    if ( !deferredPrompt ) return 'unavailable';

    const prompt = deferredPrompt;
    deferredPrompt = null;
    notify();

    try
    {
        prompt.prompt();
        const choice = await prompt.userChoice;
        return choice && choice.outcome ? choice.outcome : 'dismissed';
    }
    catch ( err )
    {
        console.error( 'install prompt failed', err );
        return 'dismissed';
    }
}

/**
 * Subscribe to install-availability changes. Fires when beforeinstallprompt
 * arrives, when the prompt is consumed, or when the app reports installed.
 * Returns an unsubscribe function.
 */
export function onInstallAvailabilityChange( callback )
{
    listeners.add( callback );
    return () => listeners.delete( callback );
}

/**
 * Register the service worker. Safe to call from window.onload — fire and
 * forget. No-op if the browser doesn't support service workers.
 */
export function registerServiceWorker()
{
    if ( !( 'serviceWorker' in navigator ) ) return;

    navigator.serviceWorker.register( '/sw.js' ).catch( err =>
    {
        console.warn( 'service worker registration failed', err );
    });
}

/**
 * Ask the browser to mark our storage as persistent so localStorage isn't
 * evicted under disk pressure. Fire and forget; resolves with whether the
 * request was granted.
 */
export async function persistStorage()
{
    try
    {
        if ( navigator.storage && typeof navigator.storage.persist === 'function' )
        {
            return await navigator.storage.persist();
        }
    }
    catch ( err )
    {
        console.warn( 'storage.persist() failed', err );
    }
    return false;
}
